import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { UserProvider } from './context/UserContext';
import { GameProvider } from './context/GameContext';
import { AuthProvider } from './context/AuthContext';
import { BattleProvider } from './context/BattleContext';
import { FocusModeProvider, useFocusMode } from './context/FocusModeContext';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SEOHelmet from './components/SEOHelmet';
import WebVitals from './components/WebVitals';
import { analytics } from './utils/analytics';
import Home from './pages/Home';
import TypingGame from './pages/TypingGame';
import Battle from './pages/Battle';
import BattleHub from './pages/BattleHub';
import BattleRoom from './pages/BattleRoom';
import CreateBattleRoom from './pages/CreateBattleRoom';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Premium from './pages/Premium';
import Settings from './pages/Settings';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';

// Component to track page views
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  
  React.useEffect(() => {
    analytics.pageView(location.pathname + location.search);
  }, [location]);
  
  return null;
};

// Main app content with focus mode support
const AppContent: React.FC = () => {
  const { isFocusMode } = useFocusMode();
  const { isDesktop } = useDeviceDetection();
  const location = useLocation();
  const isTypingPage = location.pathname === '/typing';
  
  // Redirect non-desktop devices to About page only
  if (!isDesktop && location.pathname !== '/about') {
    return <Navigate to="/about" replace />;
  }
  
  return (
    <div className="min-h-screen bg-background text-text font-sans flex flex-col">
      {/* Hide Navigation in focus mode on typing page */}
      {!(isTypingPage && isFocusMode) && <Navigation />}
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/typing" element={<TypingGame />} />
          <Route path="/battle" element={<BattleHub />} />
          <Route path="/battle/hub" element={<BattleHub />} />
          <Route path="/battle/create" element={<CreateBattleRoom />} />
          <Route path="/battle/room" element={<BattleRoom />} />
          <Route path="/battle/room/:roomId" element={<BattleRoom />} />
          <Route path="/battle/old" element={<Battle />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      
      {/* Hide Footer in focus mode on typing page */}
      {!(isTypingPage && isFocusMode) && <Footer />}
      
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#2B2D31',
            color: '#D1D0C5',
            border: '1px solid #646669',
          },
          success: {
            iconTheme: {
              primary: '#E2B714',
              secondary: '#2B2D31',
            },
          },
          error: {
            iconTheme: {
              primary: '#CA4754',
              secondary: '#2B2D31',
            },
          },
        }}
      />
    </div>
  );
};

function App() {
  // Initialize analytics on mount
  React.useEffect(() => {
    // Add your Google Analytics tracking ID here
    // analytics.init('G-XXXXXXXXXX');
    analytics.init();
  }, []);
  
  return (
    <UserProvider>
      <GameProvider>
        <AuthProvider>
          <BattleProvider>
            <FocusModeProvider>
              <Router>
                <SEOHelmet />
                <WebVitals />
                <AnalyticsTracker />
                <ScrollToTop />
                <AppContent />
              </Router>
            </FocusModeProvider>
          </BattleProvider>
        </AuthProvider>
      </GameProvider>
    </UserProvider>
  );
}

export default App;
